import { SlideDown, Switch, Textarea } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import { useField } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { Tag, UserMeta } from "types";
import { api } from "../../api/api";
import { useUser } from "../../hooks/useAccount";
import { isCommentRequired } from "../../models/Payment";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { TagInputField } from "../Field/TagInputField";
import { getCustomPaymentMethod } from "@/models/Seller";
import { getSeller } from "@/utils/getSeller";

const NoteTextarea = React.memo(() => {
    const [field, , helpers] = useField<[{ text: string }]>("note");
    const text = field.value?.[0]?.text ?? "";

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const note: [{ text: string }] = [{ text: e.target.value }];
        helpers.setValue(note);
        const trimmed = e.target.value.trim();
        useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
            draft.notes = trimmed.length > 0 ? note : undefined;
        });
    };

    return (
        <Textarea
            data-testid="create-purchase-internal-note-fields-purchase-note-field"
            value={text}
            onChange={handleChange}
        />
    );
});

NoteTextarea.displayName = "NoteTextarea";

const syncComment = (value: string) => {
    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
        draft.comment = value;
    });
};

const syncTags = (tags: Tag[]) => {
    useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
        draft.tags = tags;
    });
};

export const CreatePurchaseInternalNoteFields = React.memo(() => {
    const user = useUser();
    const seller = getSeller();

    const paymentMethod = useCreatePurchaseStore((state) => state.paymentLineItem.payment.method);
    const value = getCustomPaymentMethod(seller, paymentMethod);

    const commentRequired = isCommentRequired(seller, paymentMethod);
    const enabled = user.meta ? !!get(user, "meta.xola.seller.preferences.reviewAndPay.alwaysShowNotes") : false;

    const hasExistingNotes = () => {
        const { purchase } = useCreatePurchaseStore.getState();
        return (
            (purchase.notes?.[0]?.text.trim().length ?? 0) > 0 || !!purchase.comment || (purchase.tags?.length ?? 0) > 0
        );
    };

    const [showNotes, setShowNotes] = useState(commentRequired || enabled || hasExistingNotes());

    useEffect(() => {
        if (commentRequired) {
            setShowNotes(true);
        }
    }, [commentRequired]);

    const { mutateAsync: savePreference } = useMutation(async (payload: UserMeta) => {
        return api.post(`/users/${user.id}/meta`, payload);
    });

    const toggleNote = () => {
        const next = !showNotes;
        setShowNotes(next);
        savePreference({ xola: { seller: { preferences: { reviewAndPay: { alwaysShowNotes: next } } } } });
    };

    const showComment = value?.comment?.enabled;

    return (
        <div>
            <h3 className="mb-4">
                <Switch.Group>
                    <Switch.Label direction="left" className="mr-3">
                        Internal Notes
                    </Switch.Label>
                    {!commentRequired && (
                        <Switch
                            isChecked={showNotes}
                            size="medium"
                            data-testid="create-purchase-internal-note-fields-toggle"
                            onChange={toggleNote}
                        />
                    )}
                </Switch.Group>
            </h3>

            <SlideDown isOpen={showNotes}>
                {showComment && (
                    <FormField name="comment" label="Comment">
                        <InputField
                            placeholder={value?.comment?.title}
                            data-testid="create-purchase-internal-note-fields-comment-field"
                            onChange={syncComment}
                        />
                    </FormField>
                )}
                <FormField name="tags" label="Purchase Tags">
                    <TagInputField placeholder="Tag" onChange={syncTags} />
                </FormField>

                <FormField name="note" label="Purchase Note">
                    <NoteTextarea />
                </FormField>
            </SlideDown>
        </div>
    );
});

CreatePurchaseInternalNoteFields.displayName = "CreatePurchaseInternalNoteFields";
